/**
 * Converts structured filmmaking metadata into effective image generation prompts.
 * The "secret sauce" — transforms shot metadata into Seedream-optimized prompts.
 */

import type { PromptContext, AiStyle } from '@/types/ai.ts';
import type { TimelineShot } from '@/types/timeline.ts';

// ─── Mapping tables ──────────────────────────────────────────────────────

const SHOT_SIZE_MAP: Record<string, string> = {
  'ECU': 'extreme close-up',
  'Choker': 'choker close-up',
  'Close-Up': 'close-up',
  'Med CU': 'medium close-up',
  'Medium': 'medium shot',
  'Cowboy': 'cowboy shot',
  'Med Full': 'medium full shot',
  'Med Wide': 'medium wide shot',
  'Full': 'full shot',
  'Wide': 'wide shot',
  'Long': 'long shot',
  'Extreme Wide': 'extreme wide shot',
  'Extreme Long': 'extreme long shot',
  'Establishing': 'establishing shot',
  'Aerial': 'aerial shot',
};

const SHOT_ANGLE_MAP: Record<string, string> = {
  'Eye Level': 'eye level',
  'Low Angle': 'low angle',
  'High Angle': 'high angle',
  'Hip Level': 'hip level',
  'Knee Level': 'knee level',
  'Ground Level': 'ground level',
  'Shoulder Level': 'shoulder level',
  "Bird's Eye": "bird's eye view",
  'Dutch': 'dutch angle',
  'Overhead': 'overhead',
  'Aerial': 'aerial angle',
  'Helicopter': 'helicopter shot',
  'Drone': 'drone shot',
  'OTS': 'over-the-shoulder',
};

const SHOT_MOVEMENT_MAP: Record<string, string> = {
  'Dolly In': 'camera pushing in',
  'Dolly Out': 'camera pulling out',
  'Pan Left': 'panning left',
  'Pan Right': 'panning right',
  'Tilt Up': 'tilting up',
  'Tilt Down': 'tilting down',
  'Crane Up': 'crane shot rising',
  'Crane Down': 'crane shot descending',
  'Steadicam': 'smooth tracking shot',
  'Handheld': 'handheld, natural movement',
  'Static': 'static locked-off shot',
};

export function lensDescription(focalLength: number): string {
  if (focalLength <= 16) return 'ultra-wide lens';
  if (focalLength <= 35) return 'wide-angle lens';
  if (focalLength <= 50) return 'standard lens';
  if (focalLength <= 85) return 'portrait lens';
  return 'telephoto lens';
}

function depthOfField(fStop: number): string {
  if (fStop <= 2.8) return 'shallow depth of field';
  if (fStop <= 5.6) return 'moderate depth of field';
  return 'deep depth of field';
}

// ─── Context builder ─────────────────────────────────────────────────────

/** Build PromptContext from store state for a given shot */
export function buildPromptContext(
  shot: TimelineShot,
  scene: { name: string; locationType: string; timeOfDay: string },
  style: AiStyle | null = null,
  customPrompt?: string,
): PromptContext {
  return {
    sceneName: scene.name,
    locationType: scene.locationType,
    timeOfDay: scene.timeOfDay,
    shotSize: shot.shotSize,
    shotAngle: shot.shotAngle,
    shotMovement: shot.shotMovement,
    focalLength: shot.focalLength,
    fStop: shot.fStop,
    notes: shot.notes,
    hasPlaceholderProps: shot.hasPlaceholderProps,
    style,
    customPrompt,
  };
}

// ─── Prompt assembly ─────────────────────────────────────────────────────

/** Default base prompt — exported so the store can show it as the initial editable value */
export const DEFAULT_BASE_PROMPT =
  `Transform @image1 into a photorealistic scene with cinematic lighting true to the original composition. ` +
  `Detailed realistic faces. ` +
  `Maintain 1:1 spatial blocking, camera height, and background geometry. ` +
  `Replace stock avatars with anatomically correct human figures in the same exact coordinates. ` +
  `No text, titles, frame borders, or overlays.`;

/** Style preset prompts — each preserves composition/blocking/framing */
export const STYLE_PRESET_PHOTO = DEFAULT_BASE_PROMPT;

export const STYLE_PRESET_SKETCH =
  `Convert @image1 into a pencil sketch with natural graphite lines, cross-hatching, and visible paper texture. ` +
  `Maintain 1:1 spatial blocking, camera height, framing, and background geometry. ` +
  `Replace stock avatars with hand-drawn human figures in the same exact coordinates. ` +
  `No text, titles, frame borders, or overlays.`;

export const STYLE_PRESET_ANIME =
  `Transform @image1 into a high-quality anime illustration with clean cel-shaded coloring, bold outlines, and expressive stylized features. ` +
  `Maintain 1:1 spatial blocking, camera height, framing, and background geometry. ` +
  `Replace stock avatars with anime-styled human figures in the same exact coordinates. ` +
  `Rich background detail in anime art style. ` +
  `No text, titles, frame borders, or overlays.`;

/**
 * 20260902 - Owner copy, verbatim. Appended when the shot still holds blocking-solver placeholders:
 * labeled crate stand-ins, drawn as near-black boxes with a gold dashed edge and the prop's name
 * (see TimelineShot.hasPlaceholderProps). Without this the model faithfully preserves the crates —
 * "maintain 1:1 spatial blocking" is what every base prompt above tells it to do — and the render
 * comes back with gold-edged boxes where the furniture should be.
 *
 * Sibling in intent to the presets' own "Replace stock avatars with … figures" clause: both describe
 * something about the INPUT IMAGE that must not survive the render, which is why this one is
 * appended even when metadata is off, and why it belongs to Enhance only. The from-scratch and video
 * paths never see it: text-to-image has no input image at all, and a video is generated FROM the
 * already-enhanced still, where the crates are gone.
 */
export const PLACEHOLDER_PROPS_CLAUSE = 'Replace gold edges box props with real named props';

// ─── Text-only (from-scratch) prompts ────────────────────────────────────

/**
 * Assembly for a shot generated from the user's description alone, with no input image.
 *
 * NONE of the enhance presets above can be reused here. Each one names `@image1`, pins "Maintain
 * 1:1 spatial blocking", and — worst — orders the model to "Replace stock avatars with … figures in
 * the same exact coordinates", which with no input image means inserting PEOPLE into whatever the
 * user asked for. These are modelled instead on buildSceneBackgroundPrompt
 * (integration/generateSceneBackground.ts), which was written for the from-scratch case.
 */
export const TEXT_TO_IMAGE_NO_OVERLAYS = `No text, titles, frame borders, or overlays.`;

/**
 * The user's text verbatim, plus the one preset clause that still applies.
 *
 * NO style suffix, deliberately (owner, 2026-08-21): with no input image the typed description is
 * the SOLE authority over the look — "if users are looking for a specific style they want to
 * generate with they can enter it". The Photo/Sketch buttons are disabled in this mode for the same
 * reason, so there is no style selection left to append.
 *
 * The overlays clause is NOT a style instruction and stays: it suppresses watermarks, captions and
 * frame borders, i.e. a deliverable-format rule, and buildSceneBackgroundPrompt keeps the
 * equivalent clause for exactly the same reason.
 */
export function buildTextToImagePrompt(userPrompt: string): string {
  return [userPrompt.trim(), TEXT_TO_IMAGE_NO_OVERLAYS].filter(Boolean).join(' ');
}

const LOCATION_NATURAL: Record<string, string> = {
  'INT': 'interior',
  'EXT': 'exterior',
  'INT/EXT': 'interior/exterior',
};

const TIME_NATURAL: Record<string, string> = {
  'DAY': 'daytime',
  'NIGHT': 'nighttime',
  'TWILIGHT': 'twilight',
};

/** Build metadata suffix from shot/scene context.
 *  Natural language format: "Psycho Shower, interior scene, daytime. Janet recoils in horror" */
export function buildMetadataSuffix(ctx: PromptContext): string {
  // Scene description in natural language
  const parts: string[] = [];
  if (ctx.sceneName) parts.push(ctx.sceneName);
  if (ctx.locationType) {
    const loc = LOCATION_NATURAL[ctx.locationType] ?? ctx.locationType.toLowerCase();
    parts.push(`${loc} scene`);
  }
  if (ctx.timeOfDay) parts.push(TIME_NATURAL[ctx.timeOfDay] ?? ctx.timeOfDay.toLowerCase());
  const header = parts.join(', ');

  // Notes
  let notesLine = '';
  if (ctx.notes) {
    const clean = stripMachineBlocks(ctx.notes).trim();
    if (clean) notesLine = clean;
  }

  return [header, notesLine].filter(Boolean).join('. ');
}

/** Build the image enhancement prompt from structured context */
export function buildEnhancePrompt(ctx: PromptContext, useMetadata = true, customBasePrompt?: string): string {
  // If style has a custom prefix, use that as the base instead
  if (ctx.style?.promptPrefix) {
    return buildCustomStylePrompt(ctx);
  }

  const rawBase = customBasePrompt || DEFAULT_BASE_PROMPT;

  // 20260902 - The placeholder clause rides with the BASE, not with the metadata suffix, and so is
  // appended on BOTH branches below: it is a statement about the input image (the render still shows
  // labeled crates), not a statement about the shot's metadata, exactly like the base's own "Replace
  // stock avatars…" clause. Turning metadata off must not silently re-authorize gold boxes.
  const base = ctx.hasPlaceholderProps ? `${rawBase} ${PLACEHOLDER_PROPS_CLAUSE}.` : rawBase;

  if (!useMetadata) {
    return base;
  }

  const sceneDesc = buildMetadataSuffix(ctx);

  return `${base} ${sceneDesc}.`;
}

export const TIME_LIGHTING_MAP: Record<string, string> = {
  'DAY': 'Natural daylight with soft ambient fill',
  'NIGHT': 'Moody night lighting with practical sources and deep shadows',
  'TWILIGHT': 'Golden hour twilight with warm rim lighting',
};

/**
 * Strip the machine-written blocks from notes before they reach an image model.
 *
 * [TIMELINE] is previsSaver's standalone-save block (shot metadata). [COVERAGE] is the blocking
 * solver's "Why these shots?" rationale, filed by SceneBoardExpanded on a coverage apply — prose
 * about camera geometry ("a medium close-up at eye level, framed with a 50mm lens") that would
 * otherwise be fed into every subsequent Enhance/Create prompt for that shot. Only the user's own
 * words belong in a prompt.
 */
function stripMachineBlocks(notes: string): string {
  return notes
    .replace(/\[TIMELINE\][\s\S]*?\[\/TIMELINE\]/g, '')
    .replace(/\[COVERAGE\][\s\S]*?\[\/COVERAGE\]/g, '')
    .trim();
}

/** Fallback for custom style prefix — simpler comma-separated format */
function buildCustomStylePrompt(ctx: PromptContext): string {
  const parts: string[] = [];

  parts.push(ctx.style!.promptPrefix);

  const location = [ctx.locationType, ctx.sceneName].filter(Boolean).join(' ');
  if (location) parts.push(location);
  if (ctx.timeOfDay) parts.push(ctx.timeOfDay.toLowerCase());

  if (ctx.shotSize) parts.push(SHOT_SIZE_MAP[ctx.shotSize] ?? ctx.shotSize.toLowerCase());
  if (ctx.shotAngle) parts.push(SHOT_ANGLE_MAP[ctx.shotAngle] ?? ctx.shotAngle.toLowerCase());

  parts.push(`${Math.round(ctx.focalLength)}mm ${lensDescription(ctx.focalLength)}`);
  parts.push(depthOfField(ctx.fStop));

  if (ctx.notes) {
    const clean = stripMachineBlocks(ctx.notes).trim();
    if (clean) parts.push(clean);
  }
  // 20260902 - Same input-image fact as on the default path, in this list's comma-separated idiom.
  // A custom style prefix replaces the base prompt outright, so without this line a styled Enhance
  // is the one path that would still render the solver's gold-edged crates.
  if (ctx.hasPlaceholderProps) parts.push(PLACEHOLDER_PROPS_CLAUSE);
  if (ctx.customPrompt) parts.push(ctx.customPrompt);
  if (ctx.style!.promptSuffix) parts.push(ctx.style!.promptSuffix);

  return parts.join(', ');
}

// ─── Default transition video prompt ────────────────────────────────────

export const DEFAULT_TRANSITION_VIDEO_PROMPT =
  `Smoothly animate a continuous camera move from the first frame to the last frame. ` +
  `Maintain consistent lighting, environment, and character appearance throughout the motion.`;

/** Build prompt for first-last-frame transition video generation */
export function buildTransitionVideoPrompt(
  fromShot: TimelineShot,
  toShot: TimelineShot,
  scene: { name: string; locationType: string; timeOfDay: string },
  useMetadata = true,
): string {
  if (!useMetadata) return DEFAULT_TRANSITION_VIDEO_PROMPT;

  const parts: string[] = [];

  // Camera movement from fromShot is the primary motion cue
  if (fromShot.shotMovement && fromShot.shotMovement !== 'Static') {
    const mapped = SHOT_MOVEMENT_MAP[fromShot.shotMovement] ?? fromShot.shotMovement.toLowerCase();
    parts.push(`Smooth ${mapped} transitioning from the first composition to the second`);
  } else {
    parts.push('Smooth camera move from the first frame composition to the last frame');
  }

  // Scene context
  const sceneParts: string[] = [];
  if (scene.locationType) sceneParts.push(scene.locationType.toLowerCase());
  if (scene.timeOfDay) sceneParts.push(scene.timeOfDay.toLowerCase());
  if (scene.name) sceneParts.push(scene.name);
  if (sceneParts.length > 0) parts.push(sceneParts.join(', '));

  // Shot framing transition description
  const fromSize = fromShot.shotSize ? (SHOT_SIZE_MAP[fromShot.shotSize] ?? fromShot.shotSize.toLowerCase()) : null;
  const toSize = toShot.shotSize ? (SHOT_SIZE_MAP[toShot.shotSize] ?? toShot.shotSize.toLowerCase()) : null;
  if (fromSize && toSize && fromSize !== toSize) {
    parts.push(`moving from ${fromSize} to ${toSize}`);
  } else if (fromSize) {
    parts.push(fromSize);
  }

  // Lens + lighting
  parts.push(`${lensDescription(fromShot.focalLength)}, ${depthOfField(fromShot.fStop)}`);
  const lighting = TIME_LIGHTING_MAP[scene.timeOfDay] ?? 'cinematic lighting';
  parts.push(lighting);

  return `${DEFAULT_TRANSITION_VIDEO_PROMPT} ${parts.join('. ')}.`;
}

// ─── Default video prompt ───────────────────────────────────────────────

export const DEFAULT_VIDEO_PROMPT =
  `Smoothly animate the scene from the source image with cinematic motion. ` +
  `Maintain consistent character poses, lighting, and composition throughout.`;

/** Build video generation prompt from structured context */
export function buildVideoPrompt(ctx: PromptContext, useMetadata = true, customBasePrompt?: string): string {
  const base = customBasePrompt || DEFAULT_VIDEO_PROMPT;

  if (!useMetadata) return base;

  const parts: string[] = [];

  // Camera movement is the most important cue for video gen
  if (ctx.shotMovement && ctx.shotMovement !== 'Static') {
    const mapped = SHOT_MOVEMENT_MAP[ctx.shotMovement] ?? ctx.shotMovement.toLowerCase();
    parts.push(`Smooth ${mapped}`);
  } else {
    parts.push('Subtle ambient motion, locked-off camera');
  }

  // Scene context
  const sceneParts: string[] = [];
  if (ctx.locationType) sceneParts.push(ctx.locationType.toLowerCase());
  if (ctx.timeOfDay) sceneParts.push(`${ctx.timeOfDay.toLowerCase()}`);
  if (sceneParts.length > 0) parts.push(sceneParts.join(', '));

  // Shot framing
  if (ctx.shotSize) {
    parts.push(SHOT_SIZE_MAP[ctx.shotSize] ?? ctx.shotSize.toLowerCase());
  }
  if (ctx.shotAngle && ctx.shotAngle !== 'Eye Level') {
    parts.push(SHOT_ANGLE_MAP[ctx.shotAngle] ?? ctx.shotAngle.toLowerCase());
  }

  // Lens characteristics
  parts.push(`${lensDescription(ctx.focalLength)}, ${depthOfField(ctx.fStop)}`);

  // Lighting
  const lighting = TIME_LIGHTING_MAP[ctx.timeOfDay] ?? 'cinematic lighting';
  parts.push(lighting);

  // Notes (stripped of the machine-written blocks)
  if (ctx.notes) {
    const clean = stripMachineBlocks(ctx.notes).trim();
    if (clean) parts.push(clean);
  }

  return `${base} ${parts.join('. ')}.`;
}
